import {APIService} from '@/services/api.service';
import {COMMON_DEBOUNCE_TIME_MS} from '@/config/common-constants';
import {
  getAllSolutionFilters,
  getMissingFilters,
  getSessionId,
  getSolutionContext,
  removeMissingFilter,
  toggleSolutionTags,
} from '@/store/state/helpers';
import {FilterSection, FilterTag} from '@/views/amex/app-types';
import {groupFilterSections, DiningIcon, MarkerIcon, StayIcon} from '@mobi-components/amex-bluesky-components';
import {UserActivityCategoryEnum} from '@mobi-components/global-solution';
import {computed, onBeforeUnmount, onMounted, Ref, ref} from '@vue/composition-api';
import debounce from 'awesome-debounce-promise';

import FlightIcon from '../../svg/airplane2.vue';
import {mapFilterToFilterTag} from '@/store/api/download-domains';
import {useParams} from '@/router/router';

export function useInterestFilter(
  category: Ref<UserActivityCategoryEnum>,
  loadAdditionalExperiences: () => void = () => null,
) {
  const activatedFilters = ref<Record<string, boolean>>({});
  const filterValue = ref('');
  const activeFilterSection = ref('');
  const isSectionVisible = ref(true);
  const filterOptions = ref([]);
  const params = useParams();

  const context = computed(() => {
    return params.value.context || getSolutionContext();
  });

  const loadFilters = (keyword: string) => {
    return APIService.get('domain/filter/suggestions', {
      params: {
        mode: process.env.VUE_APP_MODE,
        category: category.value,
        keyword,
        session_id: getSessionId(),
        context: context.value,
      },
    });
  };

  const debouncedFilters = debounce(loadFilters, COMMON_DEBOUNCE_TIME_MS);

  const onFilterValueChange = (v: Event) => {
    const value = (v.target as HTMLInputElement)?.value;
    filterValue.value = value;
  };

  const onSearchFilter = async (v: string) => {
    if (!v) {
      filterOptions.value = [];
      filterValue.value = v;
      return;
    }
    // @ts-ignore
    const {candidates} = await debouncedFilters(v);
    filterValue.value = v;
    const values = candidates.map(mapFilterToFilterTag);
    const ids = new Set();
    filterOptions.value = values.filter((x: FilterTag) => {
      if (ids.has(x.id)) {
        return false;
      }
      ids.add(x.id);
      return true;
    });
  };

  const availableTags = computed<FilterTag[]>(() => {
    return getAllSolutionFilters();
  });

  const closeFiltersDropdown = () => {
    filterOptions.value = [];
  };

  onMounted(() => {
    activatedFilters.value = availableTags.value.reduce((res: Record<string, boolean>, x) => {
      res[x.id] = x.value;
      return res;
    }, {});
    document.addEventListener('click', closeFiltersDropdown);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('click', closeFiltersDropdown);
  });

  const onSelectSection = (section: FilterSection) => {
    if (activeFilterSection.value === section.id) {
      isSectionVisible.value = !isSectionVisible.value;
    } else {
      isSectionVisible.value = true;
    }
    activeFilterSection.value = section.id;
  };

  const toggleTag = async (value: FilterTag) => {
    await toggleSolutionTags([{...value, value: !value.value}]);
    activatedFilters.value[value.id] = !value.value;
    activatedFilters.value = {...activatedFilters.value};
    filterValue.value = '';
    return loadAdditionalExperiences && loadAdditionalExperiences();
  };

  const getCategorySelectedNumber = () => {
    return availableTags.value.filter((x) => activatedFilters.value[x.id]).length;
  };

  // const filterOptions = computed(() => {
  //   const tags = availableTags.value || [];
  //   return filterValue.value
  //     ? tags
  //         .filter((x) => x.domain === category.value)
  //         .filter((x) => x.name.toLowerCase().includes(filterValue.value.toLowerCase()))
  //         .map((x) => ({...x, text: domainCategoryTranslation[x.domain], isActive: activatedFilters.value[x.id]}))
  //         .slice(0, 2)
  //     : [];
  // });

  const unsatisfiedFilters = computed<FilterTag[]>(() => {
    return getMissingFilters().filter(
      (x) =>
        x.domain === category.value ||
        category.value === UserActivityCategoryEnum.NONE ||
        (category.value === UserActivityCategoryEnum.EXPERIENCE &&
          x.domain === UserActivityCategoryEnum.TRIPPREFERENCE),
    );
  });

  const removeUnsatisfiedTag = (tag: FilterTag) => {
    removeMissingFilter(tag.id);
  };

  const selectedTags = computed(() => {
    return availableTags.value.filter((x) => x.value);
  });

  const additionalTags = computed<FilterTag[]>(() => {
    const y = availableTags.value.filter(
      (x) =>
        x.domain === category.value ||
        category.value === UserActivityCategoryEnum.NONE ||
        (category.value === UserActivityCategoryEnum.EXPERIENCE &&
          x.domain === UserActivityCategoryEnum.TRIPPREFERENCE),
    );
    return y.filter((x) => x.value);
  });

  const selectedNumber = computed(() => {
    return additionalTags.value.length + unsatisfiedFilters.value.length;
  });

  const activeFilters = computed(() => {
    const sections = groupFilterSections(availableTags.value, category.value);
    return sections; //.sort((a, b) => b.options.length - a.options.length);
  });

  const availableSections = computed(() => {
    return activeFilters.value.filter((x) => x.options.length);
  });
  const activeSection = computed(() => {
    const active = activeFilters.value.find((x) => x.id === activeFilterSection.value);
    const first = activeFilters.value.find((x) => Boolean(x));
    if (!active) {
      return first;
    }
    return active;
  });
  const activeSectionId = computed(() => {
    return activeSection.value ? activeSection.value.id : '';
  });
  const availableFilterTags = computed(() => {
    const tags = (activeSection.value ? activeSection.value.options : []) || [];
    const tagsLength = tags.length;
    const rest = tags.filter((x) => !activatedFilters.value[x.id]);
    return tagsLength > 20
      ? rest.slice(0, 20).map((x) => ({...x, isActive: activatedFilters.value[x.id]}))
      : tags.slice(0, 20).map((x) => ({...x, isActive: activatedFilters.value[x.id]}));
  });

  const allFilterTags = computed(() => {
    const tags = (activeSection.value ? activeSection.value.options : []) || [];
    return tags.map((x) => ({...x, isActive: activatedFilters.value[x.id]}));
  });

  const selectedIcon = computed(() => {
    const DomainIcons: Record<string, unknown> = {
      ACCOMMODATION: StayIcon,
      EXPERIENCE: MarkerIcon,
      DINING: DiningIcon,
      TRANSPORTATION: FlightIcon,
    };

    return DomainIcons[category.value] || StayIcon;
  });

  const selectedCategory = computed(() => {
    const DomainText: Record<string, string> = {
      ACCOMMODATION: 'Stays',
      EXPERIENCE: 'Experiences',
      DINING: 'Dinings',
      TRANSPORTATION: 'Flights',
    };

    return DomainText[category.value] || 'Stays';
  });

  return {
    onSelectSection,
    toggleTag,
    getCategorySelectedNumber,
    filterOptions,
    additionalTags,
    availableSections,
    activeSectionId,
    availableFilterTags,
    allFilterTags,
    filterValue,
    onFilterValueChange,
    isSectionVisible,
    selectedNumber,
    selectedIcon,
    selectedCategory,
    activeFilterSection,
    unsatisfiedFilters,
    removeUnsatisfiedTag,
    onSearchFilter,
    selectedTags,
    context,
  };
}
